// Service for Swiss Geo Admin API integration
// Documentation: https://api3.geo.admin.ch/services/sdiservices.html

import proj4 from 'proj4'
import { apiClient } from '../apiClient'

// Define Swiss coordinate systems
// LV95 (CH1903+ / LV95) - EPSG:2056
proj4.defs(
  'EPSG:2056',
  '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs'
)

// WGS84 - EPSG:4326
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs')

interface SwissGeoFeature {
  featureId: number
  layerBodId: string
  layerName: string
  id: number
  bbox?: number[]
  geometry?: {
    points?: number[][]
    rings?: number[][][]
    spatialReference: {
      wkid: number
    }
  }
  attributes: Record<string, any>
}

interface SwissGeoResponse {
  results: SwissGeoFeature[]
}

// Convert WGS84 (lat/lon) to Swiss LV95 coordinates using proj4
export const convertWGS84ToLV95 = (
  lat: number,
  lon: number
): [number, number] => {
  // proj4 expects [lon, lat] order for geographic coordinates
  const result = proj4('EPSG:4326', 'EPSG:2056', [lon, lat])
  // Return as [x (E), y (N)] rounded to nearest meter
  return [Math.round(result[0]), Math.round(result[1])]
}

// Convert Swiss LV95 to WGS84 using proj4
export const convertLV95ToWGS84 = (x: number, y: number): [number, number] => {
  // proj4 expects [x (E), y (N)] for projected coordinates
  const result = proj4('EPSG:2056', 'EPSG:4326', [x, y])
  // Return as [lat, lon] (note the order)
  return [result[1], result[0]]
}

// Create a circle polygon for radius queries
export const createCirclePolygon = (
  centerX: number,
  centerY: number,
  radius: number,
  points: number = 32
): number[][] => {
  const polygon: number[][] = []
  const angleStep = (2 * Math.PI) / points

  for (let i = 0; i <= points; i++) {
    const angle = i * angleStep
    const x = centerX + radius * Math.cos(angle)
    const y = centerY + radius * Math.sin(angle)
    polygon.push([x, y])
  }

  return polygon
}

// Calculate distance between two points
export const calculateDistance = (
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

class SwissGeoService {
  private baseUrl = 'https://api3.geo.admin.ch/rest/services/api/MapServer'

  // Get public transport stops within a radius
  async getPublicTransportStops(
    lat: number,
    lon: number,
    radius: number = 1000
  ): Promise<SwissGeoFeature[]> {
    try {
      const [x, y] = convertWGS84ToLV95(lat, lon)
      const polygon = createCirclePolygon(x, y, radius)

      const params = new URLSearchParams({
        geometry: JSON.stringify({ rings: [polygon] }),
        geometryType: 'esriGeometryPolygon',
        layers: 'all:ch.bav.haltestellen-oev',
        imageDisplay: '500,600,96',
        mapExtent: `${x - radius},${y - radius},${x + radius},${y + radius}`,
        tolerance: '0',
        returnGeometry: 'true',
        sr: '2056',
      })

      const response = await fetch(`${this.baseUrl}/identify?${params}`)
      const data: SwissGeoResponse = await response.json()

      // Calculate distances and sort by proximity
      const stopsWithDistance = data.results.map((stop) => {
        if (stop.geometry?.points?.[0]) {
          const [stopX, stopY] = stop.geometry.points[0]
          const distance = calculateDistance(x, y, stopX, stopY)
          return {
            ...stop,
            distance: Math.round(distance),
          }
        }
        return stop
      })

      // Sort by distance
      stopsWithDistance.sort(
        (a: any, b: any) => (a.distance || Infinity) - (b.distance || Infinity)
      )

      return stopsWithDistance
    } catch (error) {
      console.error('Error fetching public transport stops:', error)
      return []
    }
  }

  // Get highway access points
  async getHighwayAccess(
    lat: number,
    lon: number,
    maxDistance: number = 10000
  ): Promise<SwissGeoFeature[]> {
    try {
      const [x, y] = convertWGS84ToLV95(lat, lon)
      const polygon = createCirclePolygon(x, y, maxDistance)

      const params = new URLSearchParams({
        geometry: JSON.stringify({ rings: [polygon] }),
        geometryType: 'esriGeometryPolygon',
        layers: 'all:ch.astra.nationalstrassenachsen',
        imageDisplay: '500,600,96',
        mapExtent: `${x - maxDistance},${y - maxDistance},${x + maxDistance},${y + maxDistance}`,
        tolerance: '0',
        returnGeometry: 'true',
        sr: '2056',
      })

      const response = await fetch(`${this.baseUrl}/identify?${params}`)
      const data: SwissGeoResponse = await response.json()

      // Filter for highway access points based on strassennummer containing "_"
      const accessPoints = data.results.filter(
        (feature) =>
          feature.attributes?.strassennummer &&
          feature.attributes.strassennummer.includes('_')
      )

      // Calculate distances to closest point on each path
      const accessWithDistance = accessPoints.map((point) => {
        if (point.geometry?.paths?.[0]) {
          // Find the closest point on the path
          const path = point.geometry.paths[0]
          let minDistance = Infinity

          for (const pathPoint of path) {
            if (Array.isArray(pathPoint) && pathPoint.length >= 2) {
              const [pointX, pointY] = pathPoint
              const distance = calculateDistance(x, y, pointX, pointY)
              if (distance < minDistance) {
                minDistance = distance
              }
            }
          }

          return {
            ...point,
            distance: Math.round(minDistance),
          }
        }
        return point
      })

      // Sort by distance
      accessWithDistance.sort(
        (a: any, b: any) => (a.distance || Infinity) - (b.distance || Infinity)
      )

      return accessWithDistance
    } catch (error) {
      console.error('Error fetching highway access:', error)
      return []
    }
  }

  // Get main roads (important roads, not highways)
  async getMainRoads(
    lat: number,
    lon: number,
    maxDistance: number = 5000
  ): Promise<SwissGeoFeature[]> {
    try {
      const [x, y] = convertWGS84ToLV95(lat, lon)
      const polygon = createCirclePolygon(x, y, maxDistance)

      const params = new URLSearchParams({
        geometry: JSON.stringify({ rings: [polygon] }),
        geometryType: 'esriGeometryPolygon',
        layers: 'all:ch.astra.hauptstrassennetz',
        imageDisplay: '500,600,96',
        mapExtent: `${x - maxDistance},${y - maxDistance},${x + maxDistance},${y + maxDistance}`,
        tolerance: '0',
        returnGeometry: 'true',
        sr: '2056',
      })

      const response = await fetch(`${this.baseUrl}/identify?${params}`)
      const data: SwissGeoResponse = await response.json()

      // Calculate distances to closest point on each path
      const roadsWithDistance = data.results.map((road) => {
        if (road.geometry?.paths?.[0]) {
          // Find the closest point on the path
          const path = road.geometry.paths[0]
          let minDistance = Infinity

          for (const pathPoint of path) {
            if (Array.isArray(pathPoint) && pathPoint.length >= 2) {
              const [pointX, pointY] = pathPoint
              const distance = calculateDistance(x, y, pointX, pointY)
              if (distance < minDistance) {
                minDistance = distance
              }
            }
          }

          return {
            ...road,
            distance: Math.round(minDistance),
          }
        }
        return road
      })

      // Sort by distance
      roadsWithDistance.sort(
        (a: any, b: any) => (a.distance || Infinity) - (b.distance || Infinity)
      )

      return roadsWithDistance
    } catch (error) {
      console.error('Error fetching main roads:', error)
      return []
    }
  }

  // Get electric vehicle charging stations
  // Note: Using fallback approach due to CORS restrictions
  // Get electric vehicle charging stations using ich-tanke-strom API
  // Get electric vehicle charging stations using ich-tanke-strom API
  // Get electric vehicle charging stations via API proxy
  async getEVChargingStations(
    lat: number,
    lon: number,
    radius: number = 5000
  ): Promise<SwissGeoFeature[]> {
    try {
      // Use our API proxy instead of calling ich-tanke-strom directly
      const params = new URLSearchParams({
        lat: lat.toString(),
        lon: lon.toString(),
        radius: radius.toString(),
      })

      const response = await apiClient.get(`/transport/ev-stations?${params}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!response.data) {
        console.warn('Transport API error:', response.statusText)
        return []
      }

      const stations = await response.data

      // Transform API response to SwissGeoFeature format
      const stationsWithDistance = stations.map(
        (station: any, index: number) => {
          return {
            featureId: index,
            layerBodId: 'ich-tanke-strom:evse',
            layerName: 'EV Charging Stations',
            id: index,
            geometry: {
              points: [[station.longitude, station.latitude]],
              spatialReference: { wkid: 4326 },
            },
            attributes: {
              name: station.name,
              operator: station.operator,
              power: station.power,
              status: station.status,
              address: station.address,
              plugTypes: station.plugTypes,
              accessibility: station.accessibility,
              isOpen24Hours: station.isOpen24Hours,
              phoneNumber: station.phoneNumber,
              paymentOptions: station.paymentOptions,
            },
            distance: station.distance,
          } as SwissGeoFeature & { distance: number }
        }
      )

      return stationsWithDistance
    } catch (error) {
      console.error('Error fetching EV charging stations via API:', error)
      return []
    }
  }

  // Calculate distance between two WGS84 coordinates using Haversine formula
  private calculateWGS84Distance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180
    const φ2 = (lat2 * Math.PI) / 180
    const Δφ = ((lat2 - lat1) * Math.PI) / 180
    const Δλ = ((lon2 - lon1) * Math.PI) / 180

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distance in meters
  }

  // Get cycling routes
  async getCyclingRoutes(
    lat: number,
    lon: number,
    radius: number = 2000
  ): Promise<SwissGeoFeature[]> {
    try {
      const [x, y] = convertWGS84ToLV95(lat, lon)
      const polygon = createCirclePolygon(x, y, radius)

      const params = new URLSearchParams({
        geometry: JSON.stringify({ rings: [polygon] }),
        geometryType: 'esriGeometryPolygon',
        layers: 'all:ch.astra.veloland',
        imageDisplay: '500,600,96',
        mapExtent: `${x - radius},${y - radius},${x + radius},${y + radius}`,
        tolerance: '5',
        returnGeometry: 'true',
        sr: '2056',
      })

      const response = await fetch(`${this.baseUrl}/identify?${params}`)
      const data: SwissGeoResponse = await response.json()

      return data.results
    } catch (error) {
      console.error('Error fetching cycling routes:', error)
      return []
    }
  }

  // Analyze public transport stops to categorize by type
  categorizePublicTransportStops(stops: SwissGeoFeature[]) {
    const categories = {
      bus: [] as any[],
      tram: [] as any[],
      train: [] as any[],
      other: [] as any[],
    }

    stops.forEach((stop) => {
      const transportType =
        stop.attributes?.verkehrsmittel_de?.toLowerCase() || ''

      let categorized = false

      // Check for each transport type independently to handle combined types like "Bus / Tram"
      if (transportType.includes('bus')) {
        categories.bus.push(stop)
        categorized = true
      }

      if (transportType.includes('tram')) {
        categories.tram.push(stop)
        categorized = true
      }

      if (transportType.includes('bahn') || transportType.includes('zug')) {
        categories.train.push(stop)
        categorized = true
      }

      if (!categorized) {
        categories.other.push(stop)
      }
    })

    return categories
  }

  // Calculate public transport quality class (Swiss ÖV-Güteklassen)
  calculatePTQualityClass(stops: SwissGeoFeature[]): string {
    if (!stops.length) return 'F'

    const nearestStop = stops[0] as any
    const distance = nearestStop.distance || Infinity

    // Simplified classification based on distance to nearest stop
    // In reality, this should also consider service frequency
    if (distance <= 300) return 'A'
    if (distance <= 500) return 'B'
    if (distance <= 750) return 'C'
    if (distance <= 1000) return 'D'
    if (distance <= 1500) return 'E'
    return 'F'
  }
}

export const swissGeoService = new SwissGeoService()
